const { Age } = require("../Constants");
const WonderNames = require("../Names/WonderNames");
const TechnologyTypes = require("../TechnologyTypes");
const { Add, AddPerWorker, AddResourceProductionThrough, CardsOf, WorkersOn, Best, Level, MilitaryTypes, TurnFlags, ProducedBy,
    AddCostModifier, CostKinds, AddTrigger, TriggerEvents, AddThrough, AddRuleException, Rules } = require("../../Effects/Effects");

const UrbanTypes = Object.values(TechnologyTypes.Urban);

// Resource cost of each stage, in build order; bonuses: the fixed bonuses printed along the bottom of the card
// (applied automatically while completed, see PlayerState.BuildDelegates); varies: stats the card changes by an amount
// that depends on the board (shown as "?" along the bottom); the card text (the rest of the card, from the BGO card
// pages); effects(player, card): the completed wonder's other contributions to the player's numbers; and
// takeCardAdjustment(player): a change to the civil actions for taking this wonder from the card row;
// onComplete(player, card): what happens once, when it is completed, returning it as text for the move log; and
// onStages(player, card, costs) / previewStages(player, card, costs): what building stages does while it is under way
// (costs: the printed costs of the stages built), and the same as text beforehand for the build button;
// expansion: for a base game wonder the New Wonders and Leaders expansion changed, what its version changes (see
// Constants/CardVersions.js).
// Pictures are found by the name without spaces or punctuation.
const Wonders = {
    [WonderNames.Colossus]: { age: Age.AgeA, stageCosts: [3, 3],
        bonuses: { strength: 2, colonization: 1 },
        text: "",
        // (the draw: GameState StartNextAge)
        expansion: { text: "At the start of Age II and Age III, draw 3 military cards.",
            effects: (player, card) => AddTrigger(player, card, TriggerEvents.AgeStart, (p, { age }) => {
                if (![Age.Age2, Age.Age3].includes(age))
                {
                    return null;
                }
                const drawn = p.gameState.DrawMilitaryCards(p, 3);
                return `${drawn} military card${drawn === 1 ? '' : 's'} drawn`;
            }) } },
    [WonderNames.HangingGardens]: { age: Age.AgeA, stageCosts: [2, 2, 2],
        bonuses: { culture: 1, happyFaces: 2 },
        text: "",
        expansion: { text: "When completed, gain 2 food.",
            onComplete: (player) => {
                player.GainFood(2);
                return "+2 food";
            } } },
    [WonderNames.LibraryOfAlexandria]: { age: Age.AgeA, stageCosts: [1, 4, 1],
        bonuses: { culture: 1, science: 1, civilHandLimit: 1, militaryHandLimit: 1 },
        text: "You may have 1 extra civil card and 1 extra military card in your hand." },
    [WonderNames.Pyramids]: { age: Age.AgeA, stageCosts: [3, 2, 1],
        bonuses: { civilActions: 1 },
        text: "" },
    // ---- Age A, New Wonders and Leaders
    [WonderNames.Colosseum]: { age: Age.AgeA, stageCosts: [2, 1, 3],
        bonuses: { happyFaces: 1, militaryActions: 1 },
        text: "You develop infantry and cavalry technologies with a science discount equal to their level. Whenever you do, score 2 culture.",
        effects: (player, card) => {
            const infantryOrCavalry = (tech) => [TechnologyTypes.Military.Infantry, TechnologyTypes.Military.Cavalry].includes(tech?.type);
            AddCostModifier(player, card, CostKinds.DevelopScience, ({ tech }) => infantryOrCavalry(tech) ? -Level(tech) : 0);
            AddTrigger(player, card, TriggerEvents.Develop, (p, { tech }) => {
                if (!infantryOrCavalry(tech))
                {
                    return null;
                }
                p.culture += 2;
                return "+2 culture";
            }, (p, { tech }) => infantryOrCavalry(tech) ? "+2 culture" : null);
        } },
    [WonderNames.RomanRoads]: { age: Age.AgeA, stageCosts: [2, 1, 1, 1],
        bonuses: { yellowTokens: 1, blueTokens: 1 }, varies: ['resources', 'science', 'culture'],
        text: "This effect varies by age: in Age I, produce 1 resource; in Age II, produce 1 science; in Age III and later, produce 2 culture.",
        // (by the age the game is in)
        effects: (player, card) => {
            const age = (p) => p.gameState?.age;
            Add(player, card, 'resources', p => age(p) === Age.Age1 ? 1 : 0);
            Add(player, card, 'science', p => age(p) === Age.Age2 ? 1 : 0);
            Add(player, card, 'culture', p => [Age.Age3, Age.Age4].includes(age(p)) ? 2 : 0);
        } },
    [WonderNames.Stonehenge]: { age: Age.AgeA, stageCosts: [1, 2, 2, 1],
        bonuses: { happyFaces: 1, culture: 1 },
        text: "For each stage of Stonehenge you build, immediately score science equal to that stage's cost.",
        onStages: (player, card, costs) => {
            const science = costs.reduce((sum, cost) => sum + cost, 0);
            player.science += science;
            return `+${science} science`;
        },
        previewStages: (player, card, costs) => `+${costs.reduce((sum, cost) => sum + cost, 0)} science` },
    [WonderNames.Acropolis]: { age: Age.AgeA, stageCosts: [2, 3],
        bonuses: { strength: 1, culture: 1, urbanBuildingLimit: 1 },
        text: "Your urban building limit is 1 higher. A peaceful change of government costs you 1 science less for each urban building you have.",
        // (a peaceful change is paid as developing the government; a revolution is not)
        effects: (player, card) => AddCostModifier(player, card, CostKinds.DevelopScience, ({ tech }, p) =>
            tech?.type === TechnologyTypes.Government ? -WorkersOn(p, UrbanTypes) : 0) },

    [WonderNames.GreatWall]: { age: Age.Age1, stageCosts: [2, 2, 3, 2],
        bonuses: { culture: 1, happyFaces: 1 }, varies: ['strength'],
        text: "You have +1 strength for each of your infantry and artillery units.",
        effects: (player, card) => AddPerWorker(player, card, 'strength', CardsOf(player, [TechnologyTypes.Military.Infantry, TechnologyTypes.Military.Artillery]), () => 1) },
    [WonderNames.StPetersBasilica]: { age: Age.Age1, stageCosts: [4, 4], expansion: { stageCosts: [1, 6, 1] },
        bonuses: { culture: 2, happyFaces: 1 },
        text: "Any other card or worker that gives you at least 1 happy face now gives you an extra 1 happy face.",
        // One extra per other card giving happy faces; for buildings, one per worker on them (credited to the building).
        // A face added to a wonder (Homer's) makes it a card with a face, once: no more if the wonder already had one
        effects: (player, card) => {
            AddPerWorker(player, card, 'happyFaces', player.technologies, (p, tech) => (tech.happyFaces ?? 0) >= 1 ? 1 : 0);
            Add(player, card, 'happyFaces', p => {
                // (buildings' own faces counted above)
                const delegates = p.happyFaceCalculationDelegate.filter(d => d.Source !== card && !(d.Source?.GetWorkerCount && !d.Through));
                const counted = new Set();
                return delegates.reduce((sum, d) => {
                    if (d.Action() < 1)
                    {
                        return sum;
                    }
                    if (d.AddedTo)
                    {
                        const own = delegates.some(other => other.Source === d.AddedTo && other.Action() >= 1);
                        if (own || counted.has(d.AddedTo))
                        {
                            return sum;
                        }
                        counted.add(d.AddedTo);
                        return sum + 1;
                    }
                    return sum + 1;
                }, 0);
            });
        } },
    [WonderNames.TajMahal]: { age: Age.Age1, stageCosts: [2, 4, 2],
        bonuses: { culture: 3, blueTokens: 1 },
        text: "If you replaced your leader this turn, taking this wonder costs you 2 civil actions less.",
        takeCardAdjustment: (player) => player.turnFlags.includes(TurnFlags.ReplacedLeader) ? -2 : 0 },
    [WonderNames.UniversitasCarolina]: { age: Age.Age1, stageCosts: [3, 3, 3],
        bonuses: { culture: 1, science: 2 },
        text: "" },
    // ---- Age I, New Wonders and Leaders
    [WonderNames.MachuPicchu]: { age: Age.Age1, stageCosts: [2, 3, 4],
        bonuses: { blueTokens: 1 }, varies: ['food', 'resources'],
        text: "Your best Age A or Age I farm and your best Age A or Age I mine produce one extra blue token.",
        effects: (player, card) => {
            const OldEnough = [Age.AgeA, Age.Age1];
            const best = (p, type) => Best(p, [type]) && p.technologies.filter(tech => tech.type === type && OldEnough.includes(tech.age) && tech.GetWorkerCount() > 0)
                .sort((a, b) => Level(b) - Level(a))[0];
            [[TechnologyTypes.Production.Farm, 'food', 'foodProductionDelegate'], [TechnologyTypes.Production.Mine, 'resources', 'resourceProductionDelegate']]
                .forEach(([type, stat, production]) => {
                    // What it adds (credited to the card it produces on), and the token itself, after the usual production
                    CardsOf(player, [type]).forEach(tech => player[{ food: 'foodProductionCalculationDelegate', resources: 'resourceProductionCalculationDelegate' }[stat]]
                        .push({ Source: card, Through: tech, Action: () => best(player, type) === tech ? tech.multiplier : 0 }));
                    player[production].push({ Source: card, Priority: -1, Action: () => {
                        const tech = best(player, type);
                        if (tech && player.blueBank > 0)
                        {
                            tech.AddContent("blueCubes", 1);
                        }
                    } });
                });
        } },
    [WonderNames.HimejiCastle]: { age: Age.Age1, stageCosts: [4, 5],
        bonuses: { strength: 2, culture: 2 },
        text: "When evaluating a war or an aggression, you may choose one of your units. Add its strength to your strength temporarily. It also gives you 3 culture if attacking or 3 strength if defending. After evaluation, sacrifice the unit." },
        // The unit: Moves PlayAggression / the defend decision (himejiUnit), and War's himejiWar decision
    [WonderNames.ForbiddenCity]: { age: Age.Age1, stageCosts: [1, 3, 3, 1],
        bonuses: { civilActions: 1, ignoredDiscontent: 2 },
        text: "Up to two of your discontent workers are ignored." },
    [WonderNames.SilkRoad]: { age: Age.Age1, stageCosts: [2, 1, 1, 1, 1, 2],
        bonuses: { culture: 2, science: 1 },
        text: "The benefit of the first action card you play on your turn is increased by 1. This effect applies as soon as the first stage is built." },
        // (while built or under way: Constants/Actions/ActionEffects.js SilkRoadBonus)

    [WonderNames.EiffelTower]: { age: Age.Age2, stageCosts: [3, 7, 3],
        bonuses: { culture: 4, happyFaces: 1 },
        text: "" },
    [WonderNames.Kremlin]: { age: Age.Age2, stageCosts: [4, 4, 4],
        bonuses: { civilActions: 1, militaryActions: 1, culture: 2, happyFaces: -1 },
        text: "" },
    [WonderNames.OceanLiner]: { age: Age.Age2, stageCosts: [4, 2, 2, 4],
        text: "During your action phase, you can gain 1 population. (This costs no civil action or food.)",
        expansion: { stageCosts: [4, 1, 1, 4], bonuses: { yellowTokens: 1 } } },
        // The action, once per turn: Moves OceanLinerPopulation
    [WonderNames.TranscontinentalRailroad]: { age: Age.Age2, stageCosts: [3, 3, 3, 3],
        bonuses: { strength: 4 }, varies: ['resources'], expansion: { bonuses: { strength: 5 } },
        text: "One of your best mines produces twice as many resources (one of the workers on it produces 2 blue tokens instead of 1).",
        effects: (player, card) => AddResourceProductionThrough(player, card, CardsOf(player, [TechnologyTypes.Production.Mine]),
            (p, mine) => Best(p, [TechnologyTypes.Production.Mine]) === mine ? mine.multiplier : 0) },

    // Age III wonders score once, when completed
    // ---- Age II, New Wonders and Leaders
    [WonderNames.HarvardCollege]: { age: Age.Age2, stageCosts: [3, 3, 5],
        bonuses: { culture: 1, science: 3 },
        text: "Once per turn, you may play an action card you took during that turn.",
        // (once a turn: Moves PlayActionCard, TurnFlags.Harvard)
        effects: (player, card) => AddRuleException(player, card, Rules.PlayActionCardsTakenThisTurn) },
    [WonderNames.StatueOfLiberty]: { age: Age.Age2, stageCosts: [7, 5],
        bonuses: { yellowTokens: 3 }, varies: ['culture'],
        text: "Your government produces culture equal to its level.",
        effects: (player, card) => AddThrough(player, card, 'culture', [player.governmentTechnology].filter(gov => gov), (p, gov) => Level(gov)) },
    [WonderNames.LouvreMuseum]: { age: Age.Age2, stageCosts: [4, 5, 4],
        bonuses: { culture: 2 }, varies: ['culture'],
        text: "The Louvre keeps its blue tokens when completed. Each can be spent at any time to gain 2 resources. The Louvre produces an extra 1 culture per blue token on it.",
        // (the tokens: its card state's, see PlayerState.cardState; spending one: Moves LouvreSpend)
        keepsTokens: true,
        effects: (player, card) => Add(player, card, 'culture', p => p.CardState(card.name).tokens ?? 0) },
    [WonderNames.SuezCanal]: { age: Age.Age2, stageCosts: [4, 3, 3, 2],
        varies: ['resources'],
        text: "When you complete this wonder, you may colonize a territory in your hand. You produce an extra 1 resource for each colony you have.",
        effects: (player, card) => Add(player, card, 'resources', p => p.colonies.length),
        // Completed: a territory from the hand may be colonized (without an auction or a sacrifice), the player's choice
        onComplete: (player) => {
            const territories = player.militaryHand.filter(c => c.type === 'Territory');
            if (territories.length === 0)
            {
                return null;
            }
            require('../../Moves/Decisions').RequestDecision(player.gameState, {
                player, type: 'suezColonize', event: WonderNames.SuezCanal, prompt: "Suez Canal: colonize a territory from your hand?", ui: 'pickCard',
                // (each as its card, to see what it gives)
                options: [...territories.map(t => ({ label: `Colonize ${t.name}`, value: t.id, card: require('../../Utilities/Converters/CardToData')(t) })), { label: "No", value: null }],
            });
            return null;
        } },

    [WonderNames.FastFoodChains]: { age: Age.Age3, stageCosts: [4, 4, 4, 4],
        text: "You immediately score 1 culture for every worker in a military unit or urban building and 2 culture for every worker in a farm or mine.",
        onComplete: (player) => Score(player, WorkersOn(player, [...MilitaryTypes, ...UrbanTypes])
            + 2 * WorkersOn(player, [TechnologyTypes.Production.Farm, TechnologyTypes.Production.Mine])) },
    [WonderNames.FirstSpaceFlight]: { age: Age.Age3, stageCosts: [1, 2, 4, 9],
        text: "You immediately score 1 culture per level of each of your technology cards.",
        // Every technology in play, including special technologies and the government
        onComplete: (player) => Score(player, player.technologies.reduce((sum, tech) => sum + Math.max(0, Level(tech)), 0)
            + Math.max(0, Level(player.governmentTechnology))) },
    [WonderNames.Hollywood]: { age: Age.Age3, stageCosts: [5, 6, 5],
        text: "You immediately score culture equal to twice the culture produced by your theaters and libraries.",
        onComplete: (player) => Score(player, 2 * ProducedBy(player.cultureProductionCalculationDelegate,
            [TechnologyTypes.Urban.Theater, TechnologyTypes.Urban.Library])) },
    [WonderNames.Internet]: { age: Age.Age3, stageCosts: [2, 3, 4, 3, 2],
        text: "You immediately score culture equal to the culture and science produced by your urban buildings plus any strength bonuses they provide.",
        onComplete: (player) => Score(player, ProducedBy(player.cultureProductionCalculationDelegate, UrbanTypes)
            + ProducedBy(player.scienceProductionCalculationDelegate, UrbanTypes)
            + ProducedBy(player.militaryCalculationDelegate, UrbanTypes)) },
}

// Scores culture for a completed wonder, returning the text for the move log
function Score(player, culture)
{
    player.culture += culture;
    return `+${culture} culture`;
}

// ---- Age III, New Wonders and Leaders
// Empire State Building: what is compared, each a culture score when the player's is higher than everyone else's
const EmpireMeasures = [
    ['science', p => p.GetScienceProduction()], ['culture', p => p.GetCultureProduction()], ['strength', p => p.GetMilitaryStrength()],
    ['food production', p => p.GetNetFoodProduction()], ['resource production', p => p.GetResourceProduction()],
];
Object.assign(Wonders, {
    [WonderNames.EmpireStateBuilding]: { age: Age.Age3, stageCosts: [17],
        text: "Compare your science, culture, and strength ratings, and your food and resource production with your opponents. For each you have higher than anyone else, score 7/8/9 culture (in a 2/3/4-player game).",
        onComplete: (player) => {
            const rivals = player.gameState.playerStates.filter(other => other !== player && !other.resigned);
            // (by the players still in the game)
            const each = { 2: 7, 3: 8 }[rivals.length + 1] ?? 9;
            const ahead = EmpireMeasures.filter(([, value]) => rivals.every(other => value(player) > value(other))).map(([name]) => name);
            player.culture += each * ahead.length;
            return ahead.length ? `+${each * ahead.length} culture (highest ${ahead.join(', ')})` : 'no culture (highest in nothing)';
        } },
    [WonderNames.UnitedNations]: { age: Age.Age3, stageCosts: [8, 8],
        text: "Score 4 culture for each wonder, each special (blue) technology, or each type of urban building you have, whichever is highest. You may reveal an Age III event from your hand, evaluate it immediately, then return it to your hand.",
        onComplete: (player) => {
            const wonders = player.wonders.length;
            const specials = player.technologies.filter(tech => Object.values(TechnologyTypes.Special).includes(tech.type)).length;
            const urbanTypes = UrbanTypes.filter(type => player.technologies.some(tech => tech.type === type && tech.GetWorkerCount() > 0)).length;
            const most = Math.max(wonders, specials, urbanTypes);
            player.culture += 4 * most;
            // An Age III event from the hand may be revealed and evaluated (it stays in the hand): the player's choice
            const events = player.militaryHand.filter(c => c.type === 'Event' && c.age === Age.Age3);
            if (events.length > 0)
            {
                require('../../Moves/Decisions').RequestDecision(player.gameState, {
                    player, type: 'unEvent', event: WonderNames.UnitedNations, prompt: "United Nations: reveal an Age III event from your hand and evaluate it now (it stays in your hand)?", ui: 'pickCard',
                    // (each as its card, picked then confirmed, as Suez Canal's territories)
                    options: [...events.map(e => ({ label: `Reveal ${e.name}`, value: e.id, card: require('../../Utilities/Converters/CardToData')(e) })), { label: "No", value: null }],
                });
            }
            return `+${4 * most} culture (${wonders} wonders, ${specials} special technologies, ${urbanTypes} urban building types: ${most} the most)`;
        } },
    [WonderNames.InternationalRedCross]: { age: Age.Age3, stageCosts: [3, 3, 3, 3, 3],
        text: "Paid in food. Other players may also work on this wonder; unlike you, they are limited to 1 stage per turn. For each stage, the player who builds it immediately scores 6 culture. When completed, you score 1 culture for each colony in play.",
        // (paid in food; others' stages: Moves RedCrossStage, each covered by its builder's blue token)
        payWith: 'food',
        onStages: (player, card, costs) => {
            player.culture += 6 * costs.length;
            return `+${6 * costs.length} culture`;
        },
        previewStages: (player, card, costs) => `+${6 * costs.length} culture`,
        onComplete: (player) => {
            const colonies = player.gameState.playerStates.reduce((sum, p) => sum + p.colonies.length, 0);
            player.culture += colonies;
            return `+${colonies} culture (${colonies} colonies in play)`;
        } },
    [WonderNames.ManhattanProject]: { age: Age.Age3, stageCosts: [4, 6, 4],
        bonuses: { strength: 13 },
        text: "You immediately score culture equal to your science rating.",
        onComplete: (player) => {
            const science = player.GetScienceProduction();
            player.culture += science;
            return `+${science} culture (science rating ${science})`;
        } },
});

module.exports = Wonders;
